import React, { useEffect } from 'react'
import _ from 'lodash'
import { useSpring, animated } from 'react-spring'
import { AutomationTarget } from '../../../types'
import {
  colorVars,
  colorLongToShort,
  lightenColor,
  getCssColorFromColorName,
  INHERIT,
} from '../../../utils/colors'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const COLOR_NAMES = _.keys(colorVars).map(colorLongToShort)
const COLOR_OPTIONS = COLOR_NAMES.concat(INHERIT, '')

export interface AnimatedProgressBarProps extends AutomationTarget {
  className?: string
  color: (typeof COLOR_OPTIONS)[number]
  percent: number
  animationSeconds?: number
  height?: number
  rounded?: boolean
}

const AnimatedProgressBar: React.FC<AnimatedProgressBarProps> = ({
  className,
  elmId,
  color: colorName,
  percent,
  animationSeconds = 0.6,
  height = 10,
  rounded = false,
}) => {
  const backgroundColor = getCssColorFromColorName(colorName)
  const pinstripeColor = lightenColor(backgroundColor, 50)

  const [barStyles, setBarStyles] = useSpring(() => ({
    from: { width: '0%' },
    to: { width: `${percent}%` },
    config: { duration: animationSeconds * 1000 }, // Duration in milliseconds
  }))

  useEffect(() => {
    setBarStyles({
      to: { width: `${percent}%` },
      config: { duration: animationSeconds * 1000 },
    })
  }, [percent, animationSeconds, barStyles, setBarStyles])

  return (
    <div
      className={cx('bar', { rounded }, className)}
      data-elm-id={elmId}
      style={{ height }}
    >
      <animated.div
        style={{
          backgroundColor,
          ...barStyles,
          height: '100%',
        }}
        className={cx('bar-inner')}
      >
        {rounded && (
          <div
            style={{
              backgroundColor: pinstripeColor,
              height: '20%',
              left: 0.4 * height,
              right: 0.4 * height,
            }}
            className={cx('pinstripe')}
          />
        )}
      </animated.div>
    </div>
  )
}

export default AnimatedProgressBar
